import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Collapse,
  FormControlLabel,
  Checkbox,
  Grid,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import { AppDispatch, RootReducerType } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  getDocumentsListThunk,
  getFileSignedURLThunk,
  getFileUploadSignedURLThunk,
  getUploadedFileByIdThunk,
  postCircleRateUpdateThunk,
  saveRowUndertakingThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import { setActiveStep } from "../redux/stepperROWSlice";
import { toast } from "react-toastify";
import UnderTackingListDetail, {
  getDocumentArray,
} from "./UnderTackingListDetail";
import { data, useNavigate } from "react-router-dom";
import { paymentThunk } from "@store/thunk/PaymentThunk";
import { StepsButton } from "./StepsButtons";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import MuiButton from "@components/Button/MuiButton";
import { fetchApplicationPreviewROW } from "@modules/application-section-row/application-preview/redux/thunk/PreviewROWThunk";
import CalculateIcon from "@mui/icons-material/Calculate";
import {
  BackupOutlined,
  FileDownload,
  Info,
  InfoOutlined,
  RemoveRedEye,
} from "@mui/icons-material";
import { fetchApplicationDetailsRow } from "@modules/provisional-payment/redux/thunk/application-detailThunk";
import { getIsCheckApplicationSubmittedThunk } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { StyledTextField } from "@components/CustomInput/customInput";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import activity_zone from "@assets/svg/activity_zone.svg";
import currency_rupee from "@assets/svg/currency_rupee.svg";

// File type interface
interface UploadedFile {
  id: string;
  file: File;
  fileName: string;
  objectKey: string;
  originalFileName: string;
  previewUrl?: string;
  uploadProgress?: number;
  isUploading?: boolean;
}

// Accordion-like Consent Card
export const CircleRateRowNew: React.FC<{ collapsed: boolean }> = ({
  collapsed,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { access_id, proposed_utility_alignment, category_id } = useSelector(
    (state: RootReducerType) => state.amenitiesROW,
  );
  const applicationPreviewROW = useSelector(
    (state: RootReducerType) => state.applicationPreviewROW?.applicationPreview,
  );
  const { activeStep } = useSelector(
    (state: RootReducerType) => state.stepperROW,
  );
  const applicationEditId = useSelector(
    (state: RootReducerType) => state.applicationDashboard?.id,
  );
  const applicationID = access_id || applicationEditId;
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [nocFeeAmount, setNocFeeAmount] = useState<number | null>(null);
  const [circleRate, setCircleRate] = useState<number | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [bankGuaranteeAmount, setBankGuaranteeAmount] = useState<number | null>(
    null,
  );
  const utilized_nh_land_area =
    (applicationPreviewROW as any)?.utilized_nh_land_area || 0;
  const proposed_utility_dimension =
    (applicationPreviewROW as any)?.proposed_utility_dimension || 0;
  const right_of_use = (applicationPreviewROW as any)?.right_of_use || 0;
  const amenity = (applicationPreviewROW as any)?.amenity || "";
  const utility_type = (applicationPreviewROW as any)?.utility_type || "";
  const utilityDuctsObj =
    applicationPreviewROW?.questions?.find(
      (item: any) => item.question_id === 383,
    )?.value === "Yes";
  const isIndustrialUtility = utility_type === "Industrial Utility";

  const isHDD =
    applicationPreviewROW?.questions?.find(
      (item: any) => item.question_id === 387,
    )?.value === "Yes";
  const isOFC = amenity === "OFC";
  // const proposed_utility_alignment = (applicationPreviewROW as any)?.proposed_utility_alignment || 0;
  const acrossHighwayValid = proposed_utility_alignment === 1;

  const makeItZero =
    !isIndustrialUtility && isHDD && isOFC && acrossHighwayValid;

  // Memoized access ID
  const backButtonClickHandler = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  // Handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: File[] = Array.from(files);

    // Validate file size (21MB limit)
    let oversizedFiles = newFiles.filter(
      (file) => file.size > 21 * 1024 * 1024,
    );
    if (oversizedFiles.length > 0) {
      event.target.value = "";
      toast.error("Some files exceed the 20MB size limit");
      return;
    }

    setIsUploading(true);

    for (const file of newFiles) {
      try {
        const fileId = `file-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`;

        // Create preview URL for images
        let previewUrl: string | undefined;
        if (file.type.startsWith("image/")) {
          previewUrl = URL.createObjectURL(file);
        }

        // Create temporary file object
        const tempFile: UploadedFile = {
          id: fileId,
          file,
          fileName: `circle_rate_${file.name}`,
          objectKey: `uploads/circle_rate_${fileId}_${file.name}`,
          originalFileName: file.name,
          previewUrl,
          uploadProgress: 0,
          isUploading: true,
        };

        setUploadedFiles((prev) => [...prev, tempFile]);

        // Get signed URL
        const fileUploadURLResponse: any = await dispatch(
          genericUploadThunk({ file, moduleType: "row" }),
        ).unwrap();

        if (!fileUploadURLResponse?.success) {
          const fileUploadURLDetails =
            fileUploadURLResponse?.payload?.response?.data?.details ||
            fileUploadURLResponse?.payload?.details;
          toast.error(
            Array.isArray(fileUploadURLDetails)
              ? fileUploadURLDetails?.[0]
              : typeof fileUploadURLDetails === "string"
                ? fileUploadURLDetails
                : "Something Went Wrong!",
          );
          event.target.value = "";
          return;
        }

        if (!productionCheck) {
          if (!fileUploadURLResponse?.data?.signedUrl) {
            throw new Error("Failed to get signed URL");
          }
          // Upload to S3
          await dispatch(
            uploadToS3Thunk({
              signedUrl: fileUploadURLResponse.data.signedUrl,
              file,
              contentType: file.type,
            }),
          ).unwrap();
        }
        if (fileUploadURLResponse?.success) {
          // Update file status to uploaded
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? {
                    ...f,
                    isUploading: false,
                    uploadProgress: 100,
                    objectKey:
                      fileUploadURLResponse?.data?.objectKey || f?.objectKey,
                  }
                : f,
            ),
          );
          toast.success(`File "${file.name}" uploaded successfully`);
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.error(`Failed to upload file: ${file.name}`);
        // Remove failed file from list
        setUploadedFiles((prev) => prev.filter((f) => f.file !== file));
      }
    }

    setIsUploading(false);
    event.target.value = ""; // Reset file input
  };

  // Handle file removal
  const handleRemoveFile = (fileId: string) => {
    const fileToRemove = uploadedFiles.find((f) => f.id === fileId);
    if (fileToRemove?.previewUrl) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    toast.success("File removed");
  };

  // Get file icon based on type
  const getFileIcon = (fileType: string, fileName: string) => {
    if (fileType.startsWith("image/")) {
      return <ImageIcon sx={{ fontSize: 25, color: "#4CAF50" }} />;
    } else if (
      fileType === "application/pdf" ||
      fileName.toLowerCase().endsWith(".pdf")
    ) {
      return <PictureAsPdfIcon sx={{ fontSize: 25, color: "#E53935" }} />;
    } else {
      return <InsertDriveFileIcon sx={{ fontSize: 25, color: "#757575" }} />;
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const SubmitCircleRate = async () => {
    try {
      if (category_id === 5) {
        const payload = {
          applicationId: applicationID,
          circle_rate: 0,
          final_license_fees_apl: 0,
          bg_amount_apl: 0,
          documents: null,
        };
        const response = await dispatch(
          postCircleRateUpdateThunk(payload),
        ).unwrap();
        if (response?.success) {
          const applicationRes = await dispatch(
            fetchApplicationDetailsRow(access_id),
          ).unwrap();
          if (!applicationRes) {
            throw new Error("Failed to fetch application details");
          }
          await dispatch(
            getIsCheckApplicationSubmittedThunk(access_id),
          ).unwrap();
          setTimeout(() => {
            navigate(
              `/application-submitted?id=${applicationRes?.application_code}`,
              { replace: true },
            );
          }, 500);
        } else {
          throw new Error(response?.message || "Submission failed");
        }
        return;
      }

      if (!circleRate) {
        toast.error("Please enter circle rate");
        return;
      }

      if (uploadedFiles.length === 0) {
        toast.error("Please upload at least one document");
        return;
      }

      // Updated validation - prevent 0
      if (
        bankGuaranteeAmount === null ||
        bankGuaranteeAmount === undefined ||
        bankGuaranteeAmount <= 0
      ) {
        toast.error(
          "Please enter a valid bank guarantee amount (must be greater than 0)",
        );
        return;
      }

      // Updated validation - prevent 0
      if (nocFeeAmount === null || nocFeeAmount === undefined) {
        toast.error("Please enter a valid NOC fee amount ");
        return;
      }

      // Check if any files are still uploading
      const stillUploading = uploadedFiles.some((file) => file.isUploading);
      if (stillUploading) {
        toast.error("Please wait for all files to finish uploading");
        return;
      }

      // Open confirmation modal instead of submitting directly
      setIsConfirmModalOpen(true);
    } catch (error: any) {
      console.error("Error submitting circle rate:", error);
      toast.error(error?.message || "Failed to submit circle rate");
    }
  };

  // Separate function for actual submission after confirmation
  const handleConfirmSubmit = async () => {
    try {
      setIsConfirmModalOpen(false);

      // Prepare documents payload from uploaded files
      const documents = uploadedFiles.map((file) => ({
        fileName: file.fileName,
        objectKey: file.objectKey,
        originalFileName: file.originalFileName,
      }));

      const payload = {
        applicationId: applicationID,
        circle_rate: circleRate,
        final_license_fees_apl: nocFeeAmount,
        bg_amount_apl: bankGuaranteeAmount,
        documents: documents,
      };

      const response = await dispatch(
        postCircleRateUpdateThunk(payload),
      ).unwrap();

      if (response?.success) {
        const applicationRes = await dispatch(
          fetchApplicationDetailsRow(access_id),
        ).unwrap();
        if (!applicationRes) {
          throw new Error("Failed to fetch application details");
        }
        await dispatch(getIsCheckApplicationSubmittedThunk(access_id)).unwrap();
        setTimeout(() => {
          navigate(
            `/application-submitted?id=${applicationRes?.application_code}`,
            { replace: true },
          );
        }, 500);
      } else {
        throw new Error(response?.message || "Submission failed");
      }
    } catch (error: any) {
      console.error("Error submitting circle rate:", error);
      toast.error(error?.message || "Failed to submit circle rate");
    }
  };

  const handleViewFile = (doc_name: any) => {
    openFileBySignedUrl({
      document_name: doc_name,
      dispatch,
      getSignedUrlThunk: getFileSignedURLThunk,
    });
  };

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, []);

  useEffect(() => {
    dispatch(fetchApplicationPreviewROW(applicationID));
  }, [dispatch]);

  // Function to determine which row should have the "Applicable" badge
  const getApplicableRow = (label: string) => {
    // If utilityDuctsObj is true, always show "Applicable" on "Utilities in existing ducts"
    if (utilityDuctsObj) {
      return label === "Utilities in existing ducts";
    }

    // If utilityDuctsObj is false, show based on right_of_use value ranges
    let applicableLabel = "";

    if (Number(proposed_utility_dimension) <= 300) {
      applicableLabel = "≤ 300 mm diameter/width";
    } else if (
      Number(proposed_utility_dimension) > 300 &&
      Number(proposed_utility_dimension) <= 1000
    ) {
      applicableLabel = "> 300 mm and ≤ 1000 mm diameter/width";
    } else if (Number(proposed_utility_dimension) > 1000) {
      applicableLabel = "> 1000 mm diameter/width";
    }

    return label === applicableLabel;
  };

  const ConfirmationModal = () => (
    <Dialog
      open={isConfirmModalOpen}
      onClose={() => setIsConfirmModalOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "6px",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: 16, color: "#212121" }}>
        Confirm Submission
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ bgcolor: "#F9FAFB", p: 2, borderRadius: 1, mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Circle Rate:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              ₹ {circleRate}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              NOC Fee Amount:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              ₹ {nocFeeAmount}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              Bank Guarantee Amount:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              ₹ {bankGuaranteeAmount}
            </Typography>
          </Box>
        </Box>

        <DialogContentText
          color={"#C9C46A"}
          bgcolor={"#FAFAED"}
          p={1}
          border={"1px solid #C9C46A"}
          borderRadius={1}
          fontSize={12}
        >
          Note: Please ensure all information (Circle Rate, Licence Fee Amount &
          Bank Guarantee information) is correct before proceeding. This action
          cannot be undone.
        </DialogContentText>

        <DialogContentText
          color="#b3261d"
          bgcolor="#f7e9e8"
          p={1}
          border="1px solid #ef8b84"
          borderRadius={1}
          fontSize={12}
          mt={1}
        >
          <strong>Important:</strong> All subsequent payments related to this
          application must be made only through the Rajmarg Pravesh Portal.
          Direct payment through BharatKosh or any offline mode is strictly not
          permitted. No refund, adjustment, reimbursement, or relaxation will be
          considered for payments made outside the portal. Please confirm that
          you understand and wish to proceed with submission.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={() => setIsConfirmModalOpen(false)}
          variant="outlined"
          sx={{
            borderRadius: "24px",
            px: 4,
            fontSize: "12px",
            textTransform: "none",
            borderColor: "#dcdcdc",
            color: "#4b4b4b",
            flex: 1,
            width: "100%",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmSubmit}
          variant="contained"
          sx={{
            borderRadius: "24px",
            fontSize: "12px",
            px: 4,
            py: 1,
            textTransform: "none",
            whiteSpace: "nowrap",
            background: "#3a1673",
            "&:hover": {
              background: "#3a1673",
            },
            flex: 1,
            width: "100%",
          }}
        >
          Confirm & Submit
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Box className="amenities-container" component="div">
        <Box className="form-wrapper mt-4">
          {category_id === 5 ? (
            <Card
              variant="outlined"
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 3 },
              }}
            >
              <Box display="flex" alignItems="start" gap={1}>
                <InfoOutlinedIcon
                  sx={{ color: "red", fontSize: 22, mt: "2px" }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Please Note:
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#374151", mt: 0.5 }}>
                    In accordance with MoRTH Policy Guidelines No.{" "}
                    <strong>145.33</strong> dated <strong>15.11.2025</strong>,
                    the following have been waived for BharatNet Applications on
                    the Rajmarg Pravesh Portal:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2.5 }}>
                    <Typography component="li" sx={{ fontSize: 13 }}>
                      License Fee: NIL
                    </Typography>
                    <Typography component="li" sx={{ fontSize: 13 }}>
                      Performance Bank Guarantee (PBG): NIL
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          ) : (
            <Card
              variant="outlined"
              sx={{ borderRadius: "12px", borderColor: "#E2E8F0" }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: { xs: 2, sm: 3 },
                  pt: 2.5,
                  pb: 1.5,
                  borderBottom: "1px solid #F1F5F9",
                }}
              >
                <Typography
                  sx={{ fontSize: 16, fontWeight: 700, color: "#212121" }}
                >
                  Enter Details to Submit Application
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#757575",
                    mt: 0.3,
                    fontWeight: 400,
                  }}
                >
                  Enter the required details for NOC Fee
                </Typography>
              </Box>

              <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                {/* ── Circle Rate ── */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 2.5 }}
                >
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{ fontSize: 16, fontWeight: 700, color: "#212121" }}
                    >
                      Enter Average Circle Rate{" "}
                      <span style={{ color: "#D93025" }}>*</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <StyledTextField
                      size="small"
                      fullWidth
                      placeholder="Rate per square meter"
                      type="number"
                      value={circleRate || ""}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (v >= 0) setCircleRate(v);
                      }}
                      onKeyDown={(e) => {
                        if (["-", "e", "E"].includes(e.key)) e.preventDefault();
                      }}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      inputProps={{ min: 0, step: "any" }}
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                          {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                        "& input[type=number]": { MozAppearance: "textfield" },
                      }}
                      InputProps={{
                        startAdornment: (
                          <Typography
                            sx={{ mr: 1, color: "#6B7280", fontWeight: 600 }}
                          >
                            ₹
                          </Typography>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {/* ── Upload ── */}
                <Grid
                  container
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ mb: 3 }}
                >
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{ fontSize: 16, fontWeight: 700, color: "#212121" }}
                    >
                      Upload Supported Circle Rate Documents{" "}
                      <span style={{ color: "#D93025" }}>*</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box
                      component="label"
                      sx={{
                        border: "1.5px dashed #A5B4CC",
                        borderRadius: "8px",
                        py: 2,
                        px: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        bgcolor: "#F8FAFF",
                        "&:hover": {
                          borderColor: "#6377A5",
                          bgcolor: "#EEF3FF",
                        },
                      }}
                    >
                      <input
                        type="file"
                        hidden
                        multiple
                        accept=".pdf"
                        onChange={handleFileUpload}
                        disabled={isUploading}
                      />
                      <BackupOutlined
                        sx={{ color: "#5B6FA6", fontSize: 26, mb: 0.4 }}
                      />
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 600, color: "#5B6FA6" }}
                      >
                        {isUploading ? "Uploading..." : "Upload Document"}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#94A3B8" }}>
                        Maximum Size: 25 MB
                      </Typography>
                    </Box>

                    {!!uploadedFiles.length && (
                      <Box sx={{ mt: 1, display: "grid", gap: 0.8 }}>
                        {uploadedFiles.map((file) => (
                          <Box
                            key={file.id}
                            sx={{
                              border: "1px solid #D9DFEA",
                              borderRadius: "6px",
                              px: 1.2,
                              py: 0.8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {getFileIcon(
                                file.file.type,
                                file.originalFileName,
                              )}
                              <Box>
                                <Typography
                                  noWrap
                                  sx={{
                                    fontSize: 12,
                                    maxWidth: { xs: 160, sm: 340 },
                                  }}
                                >
                                  {file.originalFileName}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 11, color: "#6B7280" }}
                                >
                                  {formatFileSize(file.file.size)}
                                  {file.isUploading
                                    ? ` • Uploading ${file.uploadProgress}%`
                                    : ""}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex" }}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleViewFile(
                                    file?.objectKey?.split("/").pop(),
                                  )
                                }
                                sx={{ color: "#6B7280" }}
                              >
                                <RemoveRedEye fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(file.id)}
                                disabled={file.isUploading}
                                sx={{ color: "#6B7280" }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                {/* ── Bank Guarantee Formula card ── */}
                <Box
                  sx={{
                    border: "1px solid #C9C46A",
                    borderRadius: "10px",
                    bgcolor: "#FAFAED",
                    p: { xs: 1.5, sm: 2 },
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      mb: 0.5,
                    }}
                  >
                    <InfoOutlinedIcon sx={{ color: "#A8960A", fontSize: 18 }} />
                    <Typography
                      sx={{ fontSize: 16, fontWeight: 700, color: "#928F25" }}
                    >
                      Bank Guarantee Calculation Formula
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#757575",
                      mb: 1.5,
                      fontWeight: 400,
                    }}
                  >
                    Please calculate the NOC fee using the formula below and
                    enter the amount
                  </Typography>

                  {/* Formula chips */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        border: "1px solid #C9C46A",
                        borderRadius: "6px",
                        px: 1.2,
                        py: 0.5,
                        bgcolor: "#fff",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#212121",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                      }}
                    >
                      <Box
                        component="img"
                        src={activity_zone}
                        alt="Activity zone"
                        sx={{ width: 16, height: 16 }}
                      />
                      Right of Use (meter)
                    </Box>
                    <Typography sx={{ color: "#9CA3AF", fontWeight: 700 }}>
                      ×
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid #C9C46A",
                        borderRadius: "6px",
                        px: 1.2,
                        py: 0.5,
                        bgcolor: "#fff",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#212121",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                      }}
                    >
                      <Box
                        component="img"
                        src={currency_rupee}
                        alt="Currency rupee"
                        sx={{ width: 10, height: 15 }}
                      />
                      Rate Based on Utility Dimension (Auto Calculated)
                    </Box>
                  </Box>

                  {/* Slab table + Note side-by-side */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#212121",
                          mb: 0.7,
                        }}
                      >
                        Rate Calculation slab (Auto-determined)
                      </Typography>
                      <TableContainer
                        sx={{
                          border: "1px solid #E5E7EB",
                          borderRadius: "6px",
                          overflow: "hidden",
                        }}
                      >
                        <Table sx={{ borderCollapse: "collapse" }}>
                          {/* Table header */}
                          <TableHead>
                            <TableRow
                              sx={{
                                bgcolor: "#FAFAFA",
                                borderBottom: "1px solid #E5E7EB",
                              }}
                            >
                              <TableCell
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  py: 0.8,
                                  px: 1.5,
                                }}
                              >
                                Utility Size (Diameter/Width)
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  py: 0.8,
                                  px: 1.5,
                                }}
                              >
                                Rate
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          {/* Table body */}
                          <TableBody sx={{ bgcolor: "#FFFFFF" }}>
                            {[
                              {
                                check: false,
                                label: "≤ 300 mm diameter/width",
                                rate: 100,
                              },
                              {
                                check: false,
                                label: "> 300 mm and ≤ 1000 mm diameter/width",
                                rate: 250,
                              },
                              {
                                check: false,
                                label: "> 1000 mm diameter/width",
                                rate: 500,
                              },
                              {
                                check: false,
                                label: "Utilities in existing ducts",
                                rate: 50,
                              },
                            ].map((row, index) => {
                              // Determine if this row should show "Applicable"
                              const isApplicable = getApplicableRow(row.label);

                              return (
                                <TableRow
                                  key={row.label}
                                  sx={{
                                    borderBottom:
                                      index === 3
                                        ? "none"
                                        : "1px solid #F3F4F6",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      fontSize: 12,
                                      color: "#374151",
                                      py: 0.8,
                                      px: 1.5,
                                    }}
                                  >
                                    {row.label}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontSize: 12, py: 0.8, px: 1.5 }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        ₹ {row.rate}
                                      </Typography>
                                      {isApplicable && (
                                        <Box
                                          sx={{
                                            bgcolor: "#D6EEDD",
                                            color: "#2A8642",
                                            fontSize: 12,
                                            fontWeight: 700,
                                            px: 0.8,
                                            py: 0.3,
                                            borderRadius: "2px",
                                          }}
                                        >
                                          Applicable
                                        </Box>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    {/* Note */}
                    <Grid item xs={12} md={5}>
                      <Box
                        sx={{
                          border: "1px solid #C9C46A",
                          borderRadius: "8px",
                          bgcolor: "#FEFCE8",
                          p: 1.5,
                          height: "100%",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 0.7, mb: 0.6 }}>
                          <InfoOutlinedIcon
                            sx={{ color: "#A8960A", fontSize: 16, mt: "2px" }}
                          />
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#7A6D00",
                            }}
                          >
                            Note:
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12, color: "#4B5563" }}>
                          Rate is auto-determined based on the utilized
                          dimension (diameter/width) provided in your
                          application. Please Select applicable rate from rate
                          calculation slab for calculation
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* ── Bank Guarantee Amount ── */}
                <Grid
                  container
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ mb: 3 }}
                >
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}
                    >
                      Enter Bank Guarantee Amount{" "}
                      <span style={{ color: "#D93025" }}>*</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <StyledTextField
                      size="small"
                      fullWidth
                      placeholder="Enter Amount"
                      type="number"
                      value={bankGuaranteeAmount ?? ""}
                      onChange={(e) => {
                        if (e.target.value === "") {
                          setBankGuaranteeAmount(null);
                          return;
                        }
                        const v = Number(e.target.value);
                        if (v > 0) setBankGuaranteeAmount(v);
                      }}
                      onKeyDown={(e) => {
                        if (["-", "e", "E"].includes(e.key)) e.preventDefault();
                      }}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      InputProps={{
                        startAdornment: (
                          <Typography
                            sx={{ mr: 1, color: "#6B7280", fontWeight: 600 }}
                          >
                            ₹
                          </Typography>
                        ),
                      }}
                    />
                    {/* <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.6,
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 14, color: "#9CA3AF" }}
                      />
                      <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                        Enter the calculated amount based on the formula (must be greater than 0)
                      </Typography>
                    </Box> */}
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                {/* ── NOC Fee Formula card ── */}
                <Box
                  sx={{
                    border: "1px solid #E6B84A",
                    borderRadius: "10px",
                    bgcolor: "#FFFBEB",
                    p: { xs: 1.5, sm: 2 },
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      mb: 0.5,
                    }}
                  >
                    <InfoOutlinedIcon sx={{ color: "#D97706", fontSize: 18 }} />
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 700, color: "#92400E" }}
                    >
                      NOC Fee Calculation Formula
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1.5 }}>
                    Please calculate the NOC fee using the formula below and
                    enter the amount
                  </Typography>

                  {/* Formula chips */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        border: "1px solid #D1D5DB",
                        borderRadius: "6px",
                        px: 1.2,
                        py: 0.5,
                        bgcolor: "#F9FAFB",
                        fontSize: 12,
                        fontWeight: 500,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                      }}
                    >
                      <Box
                        component="img"
                        src={activity_zone}
                        alt="Activity zone"
                        sx={{ width: 16, height: 16 }}
                      />
                      Utilized NH Land Area (sq.m)
                    </Box>
                    <Typography sx={{ color: "#9CA3AF", fontWeight: 700 }}>
                      ×
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid #D1D5DB",
                        borderRadius: "6px",
                        px: 1.2,
                        py: 0.5,
                        bgcolor: "#F9FAFB",
                        fontSize: 12,
                        fontWeight: 500,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                      }}
                    >
                      <Box
                        component="img"
                        src={currency_rupee}
                        alt="Currency rupee"
                        sx={{ width: 10, height: 15 }}
                      />
                      Circle Rate (₹ per sq.m)
                    </Box>
                    <Typography sx={{ color: "#9CA3AF", fontWeight: 700 }}>
                      ×
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid #C084FC",
                        borderRadius: "6px",
                        px: 1.2,
                        py: 0.5,
                        bgcolor: "#FAF5FF",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#7E22CE",
                      }}
                    >
                      {isIndustrialUtility ? "10%" : "1.5%"} (Per Annum)
                    </Box>
                    <Typography sx={{ color: "#9CA3AF", fontWeight: 700 }}>
                      ×
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid #93C5FD",
                        borderRadius: "6px",
                        px: 1.2,
                        py: 0.5,
                        bgcolor: "#EFF6FF",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#1D4ED8",
                      }}
                    >
                      5 Years
                    </Box>
                  </Box>

                  {/* Example row */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.8,
                      bgcolor: "#FEF3C7",
                      border: "1px solid #FDE68A",
                      borderRadius: "6px",
                      px: 1.2,
                      py: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 11, fontWeight: 700, color: "#92400E" }}
                    >
                      Example
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#92400E" }}>
                      Utilized NH Land Area (sq.m) × Prevailing Circle Rate of
                      land per unit area ×{" "}
                      {isIndustrialUtility ? "10% per annum" : "1.5% per annum"}{" "}
                      × 5 Years{" "}
                      {isIndustrialUtility
                        ? ""
                        : `, subject to minimum of Rs. 10,000/-, with 6% of annual increment`}{" "}
                      = NOC Fee Amount
                    </Typography>
                  </Box>

                  {makeItZero && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#065F46",
                        fontWeight: 600,
                        mt: 1,
                      }}
                    >
                      License fee will be waived off for this application type.
                    </Typography>
                  )}
                </Box>

                {/* ── NOC Fee Amount ── */}
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}
                    >
                      Enter NOC Fee Amount{" "}
                      <span style={{ color: "#D93025" }}>*</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <StyledTextField
                      size="small"
                      fullWidth
                      placeholder="Enter Amount (₹)"
                      type="number"
                      value={nocFeeAmount ?? ""}
                      onChange={(e) => {
                        if (e.target.value === "") {
                          setNocFeeAmount(null);
                          return;
                        }
                        const v = Number(e.target.value);
                        if (v >= 0) setNocFeeAmount(v);
                      }}
                      onKeyDown={(e) => {
                        if (["-", "e", "E"].includes(e.key)) e.preventDefault();
                      }}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      InputProps={{
                        startAdornment: (
                          <Typography
                            sx={{ mr: 1, color: "#6B7280", fontWeight: 600 }}
                          >
                            ₹
                          </Typography>
                        ),
                      }}
                    />
                    {/* <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.6,
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 14, color: "#9CA3AF" }}
                      />
                      <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                        Enter the calculated amount based on the formula (must be greater than 0)
                      </Typography>
                    </Box> */}
                  </Grid>
                </Grid>
              </Box>
            </Card>
          )}
        </Box>

        <Box className="stepper-btns gray:br-t-500 mt-3">
          <StepsButton
            onBack={backButtonClickHandler}
            onNext={SubmitCircleRate}
            nextDisabled={
              category_id === 5
                ? false
                : !circleRate ||
                  !uploadedFiles.length ||
                  bankGuaranteeAmount === null ||
                  bankGuaranteeAmount <= 0 ||
                  nocFeeAmount === null
            }
          />
        </Box>
      </Box>
      {/* Confirmation Modal */}
      <ConfirmationModal />
    </>
  );
};
